/**
 * Diagnostics for incident clip / live-feed playback failures.
 *
 * Background: a signed S3 clip URL can be fetched successfully (so the drawer
 * shows no loading state and no error) and yet the underlying <video> element
 * still fails to load or decode the media — rendering a silent black player.
 * This has been seen for machine-specific cases (e.g. PPLD / ENG-21194) where
 * clips are black but the live feed works, and is suspected again for a user in
 * T2 India loading clips hosted in US-West S3.
 *
 * The HTML5 `MediaError.code` is the key signal for telling the two root causes
 * apart:
 *   - code 2 (NETWORK)            -> S3 unreachable / corporate firewall blocking
 *                                    AWS (the "request a HAR file" scenario)
 *   - code 3 (DECODE) / code 4    -> GPU / codec / driver problem on the machine
 *     (SRC_NOT_SUPPORTED)            (the "fixed by a full Chrome reset" scenario)
 */
import { getNetworkInfo } from '@/utils/network'

export enum PlaybackFailureReason {
  // The <video> element raised an `error` event (MediaError present).
  MediaError = 'media_error',
  // The clip URL was set but playback never produced a first frame in time.
  NoFirstFrame = 'no_first_frame',
}

// HTML5 MediaError.code -> human-readable label.
// https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
const MEDIA_ERROR_LABELS: Record<number, string> = {
  1: 'aborted', // MEDIA_ERR_ABORTED
  2: 'network', // MEDIA_ERR_NETWORK
  3: 'decode', // MEDIA_ERR_DECODE
  4: 'src_not_supported', // MEDIA_ERR_SRC_NOT_SUPPORTED
}

export interface PlaybackDiagnosticsIProps {
  failure_reason: PlaybackFailureReason
  // The no-first-frame timeout (ms) that triggered a NoFirstFrame report, so the
  // event records the threshold it was judged against. Null for media errors.
  stall_threshold_ms: number | null
  media_error_code: number | null
  media_error_label: string
  media_error_message: string | null
  // 0-4, how far the media element got loading data (HAVE_NOTHING .. HAVE_ENOUGH).
  ready_state: number | null
  // 0-3 (EMPTY, IDLE, LOADING, NO_SOURCE).
  network_state: number | null
  current_time: number | null
  // Host + path only — the signed query string (AWS credentials/signature) is
  // intentionally dropped so we never log secrets to analytics.
  clip_host: string | null
  clip_path: string | null
  clip_region: string | null
  is_s3: boolean
  network_effective_type: string | null
  network_downlink_mbps: number | null
  network_rtt_ms: number | null
  user_agent: string
  platform: string
}

const sanitizeClipUrl = (
  rawUrl?: string | null
): Pick<
  PlaybackDiagnosticsIProps,
  'clip_host' | 'clip_path' | 'clip_region' | 'is_s3'
> => {
  const empty = {
    clip_host: null,
    clip_path: null,
    clip_region: null,
    is_s3: false,
  }
  if (!rawUrl) return empty
  try {
    const { hostname, pathname } = new URL(rawUrl)
    // Pulls the region out of the S3 endpoint label, tolerating the variants we
    // actually see in signed clip URLs:
    //   bucket.s3.us-west-2.amazonaws.com            (virtual-hosted)
    //   s3.us-west-2.amazonaws.com                   (path-style)
    //   s3-us-west-2.amazonaws.com                   (legacy dash)
    //   bucket.s3.dualstack.us-west-2.amazonaws.com  (dualstack — our clips bucket)
    // The `s3` label is anchored so a region-shaped substring in the bucket name
    // (e.g. hivewatch-dev-us-west-2-videoclips) is never mistaken for the region.
    const regionMatch = hostname.match(
      /(?:^|\.)s3(?:[.-](?:dualstack|fips|accelerate))*[.-]([a-z]{2}(?:-[a-z]+)+-\d+)\.amazonaws\.com$/
    )
    return {
      clip_host: hostname,
      clip_path: pathname,
      clip_region: regionMatch?.[1] ?? null,
      is_s3:
        /\.amazonaws\.com$/.test(hostname) || hostname.includes('cloudfront'),
    }
  } catch {
    return empty
  }
}

export const getPlaybackDiagnostics = (
  videoEl: HTMLVideoElement | null | undefined,
  failureReason: PlaybackFailureReason,
  clipUrl?: string | null,
  stallThresholdMs?: number | null
): PlaybackDiagnosticsIProps => {
  const mediaError = videoEl?.error ?? null
  const code = mediaError?.code ?? null
  return {
    failure_reason: failureReason,
    stall_threshold_ms: stallThresholdMs ?? null,
    media_error_code: code,
    media_error_label: code ? MEDIA_ERROR_LABELS[code] ?? 'unknown' : 'none',
    media_error_message: mediaError?.message || null,
    ready_state: videoEl ? videoEl.readyState : null,
    network_state: videoEl ? videoEl.networkState : null,
    current_time: videoEl ? Math.round(videoEl.currentTime) : null,
    ...sanitizeClipUrl(clipUrl ?? videoEl?.currentSrc),
    ...getNetworkInfo(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform: typeof navigator !== 'undefined' ? navigator.platform : '',
  }
}
